/* eslint-disable no-console */
import { useState } from 'react'

function useSessionStorage(initialPayload = {}) {
  // Initialize session storage with the initial payload
  const initializeSessionStorage = async () => {
    if (typeof window === 'undefined') {
      return
    }

    try {
      for (const [key, value] of Object.entries(initialPayload)) {
        if (!window.sessionStorage.getItem(key)) {
          if (value instanceof Blob) {
            const reader = new FileReader()
            reader.onloadend = () => {
              window.sessionStorage.setItem(key, reader.result)
            }
            reader.readAsDataURL(value)
          } else {
            window.sessionStorage.setItem(key, JSON.stringify(value))
          }
        }
      }
    } catch (error) {
      console.warn('Error initializing sessionStorage:', error)
    }
  }

  // Get from session storage then
  // parse stored json or return initialValue
  const getValue = (key, initialValue) => {
    if (typeof window === 'undefined' || !key) {
      return initialValue
    }

    try {
      const item = window.sessionStorage.getItem(key)
      if (item) {
        try {
          return JSON.parse(item)
        } catch (e) {
          return item
        }
      }
      return initialValue
    } catch (error) {
      console.warn(`Error reading sessionStorage key “${key}”:`, error)
      return initialValue
    }
  }

  // Initialize session storage with the initial payload
  initializeSessionStorage()

  // State to store our values
  const [storedValues, setStoredValues] = useState(() => {
    const values = {}
    Object.keys(initialPayload).forEach(key => {
      values[key] = getValue(key, initialPayload[key])
    })
    return values
  })

  // Return a wrapped version of useState's setter function that
  // persists the new value to sessionStorage.
  const setValue = (key, value) => {
    if (typeof window == 'undefined') {
      console.warn(
        `Tried setting sessionStorage key “${key}” even though environment is not a client`,
      )
      return
    }

    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore =
        value instanceof Function ? value(storedValues[key]) : value

      // Save state
      setStoredValues(prevValues => ({
        ...prevValues,
        [key]: valueToStore,
      }))

      // Save to session storage
      if (valueToStore instanceof Blob) {
        const reader = new FileReader()
        reader.onloadend = () => {
          window.sessionStorage.setItem(key, reader.result)
        }
        reader.readAsDataURL(valueToStore)
      } else {
        window.sessionStorage.setItem(key, JSON.stringify(valueToStore))
      }
    } catch (error) {
      console.warn(`Error setting sessionStorage key “${key}”:`, error)
    }
  }

  const deleteValue = key => {
    if (typeof window === 'undefined') {
      console.warn(
        `Tried deleting sessionStorage key “${key}” even though environment is not a client`,
      )
      return
    }

    try {
      // Remove from state
      setStoredValues(prevValues => {
        const newValues = { ...prevValues }
        delete newValues[key]
        return newValues
      })

      // Remove from session storage
      window.sessionStorage.removeItem(key)
    } catch (error) {
      console.warn(`Error deleting sessionStorage key “${key}”:`, error)
    }
  }

  return { storedValues, setValue, getValue, deleteValue }
}

export default useSessionStorage
